'use client';
// ZERO-WAIT BACKGROUND PREFETCH (Founder 2026-09-30, "0 loading times"). The server hands this ONE component the exact image
// descriptors the UI will ask for next (the same avatarImg()/wideImg() { src, srcSet, sizes } — lib/imgUrl.ts — so the browser picks
// the very same srcset candidate and the later tap is a real cache hit) plus the page links a visitor is likely to open.
// Never competes with first render: starts only after window `load` + a quiet gap + requestIdleCallback (setTimeout fallback),
// skips Save-Data / 2g / slow-2g, runs a few low-priority fetches at a time, yields to the main thread between items.
import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { avatarImg, wideImg } from '@/lib/imgUrl';
import { setZoomNatural } from '@/lib/avatarZoomSize';

/** Compact spec (keeps the RSC payload small): a = square avatar css px (avatarImg), w = [sizes, fallbackW] (wideImg), m = media query. */
export type PrefetchImage = { s: string; a?: number; w?: [string, 320 | 480 | 640 | 960]; m?: string };
const resolve = (p: PrefetchImage): { src: string; srcSet?: string; sizes?: string } =>
  p.a ? avatarImg(p.s, p.a) : p.w ? wideImg(p.s, p.w[0], p.w[1]) : { src: p.s };

type Props = { images?: PrefetchImage[]; hrefs?: string[]; linkOrder?: string; concurrency?: number; maxImages?: number; maxHrefs?: number; delayMs?: number };

const idle = (fn: () => void, timeout = 2000) => {
  const w = window as any;
  if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(fn, { timeout }); else setTimeout(fn, 200);
};

// ONE door for full page prefetches (30/09/26). A full prefetch stays fresh in the Next router cache for staleTimes.static (5 min),
// so an href prefetched in this tab in the last 4 min is skipped — Back to a category no longer re-downloads the same pro pages.
const fullDone = new Map<string, number>();
const FULL_TTL = 4 * 60_000;
export function prefetchFull(router: { prefetch: (...a: any[]) => void }, href: string): void {
  const t = fullDone.get(href);
  if (t && Date.now() - t < FULL_TTL) return;
  fullDone.set(href, Date.now());
  try { (router.prefetch as any)(href, { kind: 'full' }); } catch { /* prefetch is best-effort */ }
}
/** the visitor revealed more links (e.g. "הצגת עוד מומחים") — warm them one by one in idle time, same door */
export function prefetchFullIdle(router: { prefetch: (...a: any[]) => void }, hrefs: string[]): void {
  if (typeof window === 'undefined' || !networkAllows()) return;
  const q = hrefs.filter(h => h && h !== location.pathname);
  const one = () => { const h = q.shift(); if (!h) return; prefetchFull(router, h); idle(one, 1500); };
  idle(one, 1500);
}

function networkAllows(): boolean {
  const c = (navigator as any).connection;
  if (!c) return true;
  if (c.saveData) return false;
  return !/(^|-)2g$/.test(String(c.effectiveType || ''));   // '2g' and 'slow-2g'
}

export default function IdlePrefetch({ images = [], hrefs = [], linkOrder, concurrency = 3, maxImages = 300, maxHrefs = 8, delayMs = 1200 }: Props) {
  const router = useRouter();
  useEffect(() => {
    if (typeof window === 'undefined' || !networkAllows()) return;
    let stopped = false; let timer: ReturnType<typeof setTimeout> | undefined;
    const seen = new Set<string>();
    const queue = images.filter(p => !!p?.s && !(p.m && !window.matchMedia(p.m).matches)).map(resolve).filter(im => {
      if (!im.src || im.src.startsWith('data:')) return false;
      const k = `${im.src}|${im.srcSet || ''}|${im.sizes || ''}`; if (seen.has(k)) return false; seen.add(k); return true;
    }).slice(0, maxImages);
    // linkOrder: a CSS selector — prefetch the allowed hrefs in the order the visitor SEES them (the client re-ranks the list by city)
    const allowed = [...new Set(hrefs)].filter(h => h && h !== location.pathname);
    const orderLinks = () => { if (!linkOrder) return allowed; const pos = new Map<string, number>(); let n = 0;
      document.querySelectorAll<HTMLAnchorElement>(linkOrder).forEach(a => { const h = a.getAttribute('href') || ''; if (!pos.has(h)) pos.set(h, n++); });
      return [...allowed].sort((x, y) => (pos.get(x) ?? 1e9) - (pos.get(y) ?? 1e9)); };
    let links: string[] = [];
    const held: HTMLImageElement[] = [];   // keep references until done so nothing is GC-cancelled mid-flight

    const pump = () => {
      if (stopped) return;
      const im = queue.shift(); if (!im) return;
      const el = new Image();
      el.decoding = 'async';
      (el as any).fetchPriority = 'low';
      if (im.sizes) el.sizes = im.sizes;
      if (im.srcSet) el.srcset = im.srcSet;
      el.src = im.src;
      held.push(el);
      const next = () => { el.onload = el.onerror = null; idle(pump, 1000); };
      el.onload = () => { setZoomNatural(im.src, el.naturalWidth); next(); }; el.onerror = next;
    };
    const prefetchLinks = () => {
      links = orderLinks().slice(0, maxHrefs);
      const one = () => { if (stopped) return; const h = links.shift(); if (!h) return;
        prefetchFull(router, h);
        idle(one, 1500); };
      one();
    };
    const start = () => {
      timer = setTimeout(() => idle(() => {
        if (stopped) return;
        for (let i = 0; i < concurrency; i++) pump();
        prefetchLinks();
      }, 3000), delayMs);
    };
    if (document.readyState === 'complete') start(); else window.addEventListener('load', start, { once: true });
    return () => { stopped = true; if (timer) clearTimeout(timer); window.removeEventListener('load', start); held.length = 0; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}
